import Api from "../../../../../../../../../core/services/api/api";
import {toPartNumberUpper} from "../shared/normalize-spreadsheet-cell-value";


/**
 * @typedef {{ id: *, info: * } | null} PartInfoCacheEntry
 * `null` = we already asked the server for this part number in this run and it was not returned.
 */

/**
 * In-memory part-number → id/info cache for one import run.
 */
export class PartNumberLookupStore {
    constructor() {
        /** @type {Map<string, PartInfoCacheEntry>} */
        this._entryByPartNo = new Map();
    }

    /**
     * Drop all cached entries (e.g. when reusing context across runs).
     */
    clear() {
        this._entryByPartNo.clear();
    }

    /**
     * Single uncached round-trip to the API (used only for part numbers not yet in the run cache).
     *
     * @param {string[]} partNumbersUpper
     * @returns {Promise<{partIds: Record<string, *>, partInfo: Record<string, *>}>}
     */
    async #fetchPartInfoFromServerOnce(partNumbersUpper) {
        if (!partNumbersUpper?.length) {
            return {partIds: {}, partInfo: {}};
        }
        const response = await Api.getPartInfoWithPartNumber({partNumbers: partNumbersUpper}, false);
        if (!response?.resultFlag || !response?.data?.length) {
            return {partIds: {}, partInfo: {}};
        }
        return {
            partIds: Object.fromEntries(response.data.map((d) => [d.partNo, d.id])),
            partInfo: Object.fromEntries(response.data.map((d) => [d.partNo, d])),
        };
    }

    /**
     * @param {string[]} partNumbersUpper
     * @param {boolean} [disableCache=false] If true, forces a server request for all given part numbers (bypassing the cache).
     * @returns {Promise<{partIds: Record<string, *>, partInfo: Record<string, *>}>}
     */
    async resolve(partNumbersUpper, disableCache = false) {
        const unique = [...new Set((partNumbersUpper ?? []).map(toPartNumberUpper).filter(Boolean))];
        if (!unique.length) {
            return {partIds: {}, partInfo: {}};
        }

        const missing = unique.filter((pn) => disableCache || !this._entryByPartNo.has(pn));

        if (missing.length > 0) {
            const fresh = await this.#fetchPartInfoFromServerOnce(missing);

            for (const pn of missing) {
                let id = fresh.partIds[pn];
                let info = fresh.partInfo[pn];

                if (id == null && fresh.partIds && Object.keys(fresh.partIds).length > 0) {
                    const altKey = Object.keys(fresh.partIds).find(
                        (key) => String(key).trim().toUpperCase() === pn
                    );
                    if (altKey != null) {
                        id = fresh.partIds[altKey];
                        info = fresh.partInfo[altKey];
                    }
                }

                if (id != null && id !== '') {
                    this._entryByPartNo.set(pn, {id, info: info ?? null});
                } else {
                    this._entryByPartNo.set(pn, null);
                }
            }
        }

        const partIds = {};
        const partInfo = {};
        for (const pn of unique) {
            const entry = this._entryByPartNo.get(pn);
            if (entry) {
                partIds[pn] = entry.id;
                partInfo[pn] = entry.info;
            }
        }
        return {partIds, partInfo};
    }
}
